import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const parseTextFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    reader.onerror = () => reject(new Error('Failed to read text file'));
    reader.readAsText(file);
  });
};

export const parseDocxFile = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (error) {
    throw new Error('Failed to parse DOCX file: ' + error.message);
  }
};

export const parsePdfFile = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText.trim();
  } catch (error) {
    throw new Error('Failed to parse PDF file: ' + error.message);
  }
};

export const parseXlsxFile = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    let fullText = '';
    
    workbook.SheetNames.forEach((sheetName, index) => {
      const sheet = workbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      
      if (workbook.SheetNames.length > 1) {
        fullText += `--- Sheet: ${sheetName} ---\n\n`;
      }
      
      sheetData.forEach(row => {
        const rowText = row.filter(cell => cell !== '').join('\t');
        if (rowText.trim()) {
          fullText += rowText + '\n';
        }
      });
      
      if (index < workbook.SheetNames.length - 1) {
        fullText += '\n\n';
      }
    });
    
    return fullText.trim();
  } catch (error) {
    throw new Error('Failed to parse Excel file: ' + error.message);
  }
};

export const parseDocument = async (file) => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }

  const extension = file.name.split('.').pop().toLowerCase();
  
  switch (extension) {
    case 'txt':
      return await parseTextFile(file);
    case 'docx':
      return await parseDocxFile(file);
    case 'pdf':
      return await parsePdfFile(file);
    case 'xlsx':
    case 'xls':
      return await parseXlsxFile(file);
    default:
      throw new Error(`Unsupported file type: .${extension}. Please upload .txt, .docx, .pdf, or .xlsx files.`);
  }
};

export const validateFile = (file) => {
  const validExtensions = ['txt', 'docx', 'pdf', 'xlsx', 'xls'];
  const extension = file.name.split('.').pop().toLowerCase();
  
  if (!validExtensions.includes(extension)) {
    return { valid: false, error: `Unsupported file type. Please upload .txt, .docx, .pdf, or .xlsx files.` };
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File too large. Maximum size is 5MB.` };
  }
  
  return { valid: true };
};

export const getWordCount = (text) => {
  if (!text) return 0;
  return text.split(/\s+/).filter(word => word.length > 0).length;
};

export const splitIntoSegments = (text, segmentType = 'paragraphs') => {
  if (!text) return [];
  
  switch (segmentType) {
    case 'paragraphs':
      return text.split(/\n\n+/).filter(p => p.trim().length > 0);
    case 'sentences':
      return text.match(/[^.!?]+[.!?]+/g) || [text];
    case 'lines':
      return text.split(/\n/).filter(l => l.trim().length > 0);
    default:
      return [text];
  }
};
